import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { normalizeLanguage, translatePair } from "../../services/language";
import { setLanguage as setI18nLanguage } from "../../i18n";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import "../../components/lobby/lobbyModern.css";
import "./statsFilter.css";

// Estadísticas «Estilo 1 · Panel general». Todo sale de GET /tournaments/stats/me, que el
// servidor calcula con TODAS las partidas guardadas del jugador (o solo las del modo elegido).
const GAME_STATS_MODE_KEYS = ["ciencia", "cienzar", "sciencevsazar", "azar"];
const STATS_MODE_FILTER_STORAGE_KEY = "xima_stats_mode_filter";
const MODE_META = {
  ciencia: { label: "Ciencia", color: "#3b7cf0" },
  cienzar: { label: "Cienzar", color: "#a55cf0" },
  sciencevsazar: { label: "Contrazar", color: "#2fbf6a" },
  azar: { label: "Azar", color: "#e5484d" },
};
const GOLD = "#f6b93b";
const DONUT_COLORS = { win: "#2fbf6a", loss: "#e5484d", draw: "#6b7385" };

const readStoredModeFilter = () => {
  try {
    const stored = window.sessionStorage.getItem(STATS_MODE_FILTER_STORAGE_KEY) || "";
    return GAME_STATS_MODE_KEYS.includes(stored) ? stored : "";
  } catch {
    return "";
  }
};

const storeModeFilter = (value) => {
  try {
    window.sessionStorage.setItem(STATS_MODE_FILTER_STORAGE_KEY, value || "");
  } catch {
    // Sin almacenamiento: el filtro solo dura mientras la página esté abierta.
  }
};

const toCount = (value) => Math.max(0, Math.round(Number(value) || 0));
const numberFormatter = new Intl.NumberFormat("es-ES");
const formatCount = (value) => numberFormatter.format(toCount(value));
const formatDecimal = (value, digits = 2) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    Math.max(0, Number(value) || 0)
  );
const formatPercent = (part, total) => `${formatDecimal(total > 0 ? (toCount(part) / total) * 100 : 0, 1)} %`;

const formatTotalTime = (totalSeconds) => {
  const minutesTotal = Math.floor(toCount(totalSeconds) / 60);
  const hours = Math.floor(minutesTotal / 60);
  const minutes = minutesTotal % 60;
  return hours > 0 ? `${formatCount(hours)} h ${minutes} min` : `${minutes} min`;
};

const formatAverageTime = (totalSeconds) => {
  const safe = toCount(totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = String(safe % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}` : `${minutes}:${seconds}`;
};

const formatLongDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.valueOf())) return "";
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }).replace(".", "");
};

// Donut SVG de tres partes (victorias, derrotas, sin ganador).
const Donut = ({ wins, losses, draws, centerLabel, centerValue }) => {
  const total = wins + losses + draws;
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const segments = [
    ["win", wins],
    ["loss", losses],
    ["draw", draws],
  ]
    .filter(([, value]) => value > 0)
    .map(([key, value]) => {
      const length = (value / total) * circumference;
      const segment = { key, length, offset };
      offset += length;
      return segment;
    });

  return (
    <div className="xst-donut">
      <svg viewBox="0 0 190 190" role="img" aria-label={`${centerValue} ${centerLabel}`}>
        <circle cx="95" cy="95" r={radius} fill="none" stroke="#252a36" strokeWidth="18" />
        {segments.map((segment) => (
          <circle
            key={segment.key}
            cx="95"
            cy="95"
            r={radius}
            fill="none"
            stroke={DONUT_COLORS[segment.key]}
            strokeWidth="18"
            strokeDasharray={`${segment.length} ${circumference - segment.length}`}
            strokeDashoffset={-segment.offset}
            transform="rotate(-90 95 95)"
          />
        ))}
      </svg>
      <div className="xst-donut-center">
        <b>{centerValue}</b>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
};

const Stats = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language || "es");
  const routeActions = useModernRouteActions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [modeFilter, setModeFilter] = useState(readStoredModeFilter);
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);

  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);

  // Igual que Premium/Puzzles: se esconde el menú antiguo y queda solo el del diseño Clásico.
  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active");
    return () => document.body.classList.remove("xima-lobby-modern-active");
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get("/tournaments/stats/me", {
        params: modeFilter ? { mode: modeFilter } : {},
      });
      setPayload(response?.data?.stats || null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          t("No se pudieron cargar las estadísticas.", "Could not load stats.")
      );
    } finally {
      setLoading(false);
    }
  }, [modeFilter, t]);

  useEffect(() => {
    loadStats().catch(() => {});
  }, [loadStats]);

  // Pulsar el modo ya elegido vuelve a «Todos».
  const selectModeFilter = useCallback((nextMode) => {
    const safeMode = GAME_STATS_MODE_KEYS.includes(nextMode) ? nextMode : "";
    setModeFilter((current) => {
      const resolved = current === safeMode ? "" : safeMode;
      storeModeFilter(resolved);
      return resolved;
    });
  }, []);

  const summary = payload?.summary || {};
  const byMode = useMemo(() => payload?.byMode || {}, [payload]);
  const cimaxScores = useMemo(() => payload?.cimaxScores || {}, [payload]);
  const totalMatches = toCount(summary.totalMatches);
  const wins = toCount(summary.wins);
  const losses = toCount(summary.losses);
  const draws = toCount(summary.draws);
  const selectedMeta = modeFilter ? MODE_META[modeFilter] : null;

  const firstMatchLabel = useMemo(() => {
    const candidates = [
      payload?.allModesFirstMatchAt,
      ...GAME_STATS_MODE_KEYS.map((key) => byMode?.[key]?.firstMatchAt),
    ]
      .map((value) => (value ? new Date(value) : null))
      .filter((date) => date && !Number.isNaN(date.valueOf()));
    if (candidates.length === 0) return "";
    return formatLongDate(new Date(Math.min(...candidates.map((date) => date.valueOf()))));
  }, [payload, byMode]);

  const cimaxRows = useMemo(() => {
    const rows = GAME_STATS_MODE_KEYS.map((key) => ({ key, ...MODE_META[key], score: toCount(cimaxScores[key]) }));
    const best = rows.reduce((top, row) => (row.score > top.score ? row : top), rows[0]);
    return { rows, best, max: Math.max(1, best.score) };
  }, [cimaxScores]);

  const favoriteKey = GAME_STATS_MODE_KEYS.includes(payload?.favoriteMode) ? payload.favoriteMode : "";
  const favoriteStats = favoriteKey ? byMode[favoriteKey] || {} : {};
  const tournamentWins = modeFilter
    ? toCount(payload?.tournamentWinsByMode?.[modeFilter])
    : toCount(payload?.tournamentWinsByMode?.total);
  const selectedGameStats = modeFilter ? byMode?.[modeFilter]?.gameStats || {} : {};

  const tabs = [
    {
      key: "",
      label: t("Todos", "All"),
      line: t("Todos los modos", "All modes"),
      count: payload ? payload.allModesTotalMatches : null,
      color: GOLD,
    },
    ...GAME_STATS_MODE_KEYS.map((key) => ({
      key,
      label: MODE_META[key].label,
      line: `CIMAX ${formatCount(cimaxScores[key])}`,
      count: payload ? byMode?.[key]?.totalMatches : null,
      color: MODE_META[key].color,
    })),
  ];

  const dayStreak = toCount(summary.consecutiveDaysStreak);
  const kpis = [
    { key: "total", icon: "⏱", label: t("Tiempo total jugado", "Total play time"), value: formatTotalTime(summary.totalTimePlayedSeconds) },
    { key: "avg", icon: "⌛", label: t("Tiempo medio por partida", "Average game time"), value: formatAverageTime(summary.averageDurationSeconds) },
    {
      key: "days",
      icon: "🔥",
      label: t("Racha de días", "Day streak"),
      value: formatCount(dayStreak),
      extra: dayStreak === 1 ? t("día seguido", "day in a row") : t("días seguidos", "days in a row"),
    },
    {
      key: "streak",
      icon: "🏅",
      label: t("Mejor racha de victorias", "Best win streak"),
      value: formatCount(summary.bestWinStreak),
      extra: `${t("ahora", "now")} ${formatCount(summary.currentWinStreak)}`,
    },
  ];

  const detailMetrics = [
    [t("Fuerza máxima", "Max force"), selectedGameStats.maxGameForceAchieved],
    [t("Nivel máximo", "Max level"), selectedGameStats.maxGameLevelAchieved],
    [t("Potencia máxima", "Max potency"), selectedGameStats.maxGamePotency],
    [t("Fuerza promedio", "Average force"), selectedGameStats.averageGameForce],
    [t("Nivel promedio", "Average level"), selectedGameStats.averageGameLevel],
    [t("Potencia media", "Average potency"), selectedGameStats.averageGamePotency],
  ];

  const handleKeySelect = (key) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectModeFilter(key);
    }
  };

  const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;
  const firstLoad = !payload && loading;

  return (
    <div className="xlm-root xst-root">
      <ModernSidebar
        language={language}
        active="stats"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main xst-main">
        <div className="xst-shell">
          <header className="xst-top">
            <div className="xst-title">
              <button type="button" className="xst-back" onClick={() => navigate("/game/lobby")}>
                ‹ {t("Volver al Lobby", "Back to Lobby")}
              </button>
              <h1>{t("Estadísticas", "Statistics")}</h1>
              <p>
                {selectedMeta
                  ? t(
                      `Solo partidas de ${selectedMeta.label}${firstMatchLabel ? ` desde ${firstMatchLabel}` : ""}. Toca el modo otra vez para ver todos.`,
                      `Only ${selectedMeta.label} games${firstMatchLabel ? ` since ${firstMatchLabel}` : ""}. Tap the mode again to see all.`
                    )
                  : t(
                      `Todas tus partidas desde que te registraste${firstMatchLabel ? ` (${firstMatchLabel})` : ""}. Toca un modo para ver solo ese modo.`,
                      `All your games since you signed up${firstMatchLabel ? ` (${firstMatchLabel})` : ""}. Tap a mode to see only that mode.`
                    )}
              </p>
            </div>
            <div className="xst-actions">
              <button type="button" className="xst-ghost" onClick={() => navigate("/game/history")}>
                🕘 {t("Ir a Historial", "Go to History")}
              </button>
              <button type="button" className="xst-ghost" onClick={() => loadStats()} disabled={loading}>
                ↻ {loading ? t("Actualizando…", "Updating…") : t("Actualizar", "Refresh")}
              </button>
            </div>
          </header>

          <nav className="xst-tabs" aria-label={t("Filtrar por modo", "Filter by mode")}>
            {tabs.map((tab) => (
              <button
                key={tab.key || "all"}
                type="button"
                className={`xst-tab${modeFilter === tab.key ? " is-active" : ""}`}
                style={{ "--xst-mode": tab.color }}
                aria-pressed={modeFilter === tab.key}
                onClick={() => selectModeFilter(tab.key)}
              >
                <span className="xst-tab-text">
                  <strong>{tab.label}</strong>
                  <small>{tab.line}</small>
                </span>
                <b>{tab.count === null || tab.count === undefined ? "—" : formatCount(tab.count)}</b>
              </button>
            ))}
          </nav>

          {error ? (
            <div className="xst-card xst-message is-error" role="alert">
              <span>{error}</span>
              <button type="button" className="xst-gold-btn" onClick={() => loadStats()}>
                {t("Reintentar", "Retry")}
              </button>
            </div>
          ) : null}

          {firstLoad ? (
            <div className="xst-card xst-message">
              <span className="xst-spinner" aria-hidden="true" />
              <span>{t("Cargando tus estadísticas…", "Loading your stats…")}</span>
            </div>
          ) : payload ? (
            <div className={`xst-body${loading ? " is-refreshing" : ""}`}>
              {totalMatches === 0 ? (
                <div className="xst-card xst-message">
                  <span>
                    {selectedMeta
                      ? t(`Aún no tienes partidas de ${selectedMeta.label}.`, `You have no ${selectedMeta.label} games yet.`)
                      : t("Aún no has jugado ninguna partida.", "You haven't played any games yet.")}
                  </span>
                  <button type="button" className="xst-gold-btn" onClick={() => navigate("/game/lobby")}>
                    {t("Jugar ahora", "Play now")}
                  </button>
                </div>
              ) : null}

              <section className="xst-row xst-row-main">
                <article className="xst-card xst-summary">
                  <Donut
                    wins={wins}
                    losses={losses}
                    draws={draws}
                    centerValue={`${formatDecimal(winRate, 0)}%`}
                    centerLabel={t("Victorias", "Wins")}
                  />
                  <div className="xst-summary-body">
                    <span className="xst-label">{t("Partidas jugadas", "Games played")}</span>
                    <b className="xst-huge">{formatCount(totalMatches)}</b>
                    <div className="xst-split">
                      <div className="is-win">
                        <span className="xst-label">{t("Victorias", "Wins")}</span>
                        <b>{formatCount(wins)}</b>
                        <small>{formatPercent(wins, totalMatches)}</small>
                      </div>
                      <div className="is-loss">
                        <span className="xst-label">{t("Derrotas", "Losses")}</span>
                        <b>{formatCount(losses)}</b>
                        <small>{formatPercent(losses, totalMatches)}</small>
                      </div>
                      <div className="is-draw">
                        <span className="xst-label">{t("Sin ganador", "No winner")}</span>
                        <b>{formatCount(draws)}</b>
                        <small>{formatPercent(draws, totalMatches)}</small>
                      </div>
                    </div>
                    <div className="xst-stack" aria-hidden="true">
                      {totalMatches > 0 ? (
                        <>
                          <i style={{ width: `${(wins / totalMatches) * 100}%`, background: DONUT_COLORS.win }} />
                          <i style={{ width: `${(losses / totalMatches) * 100}%`, background: DONUT_COLORS.loss }} />
                          <i style={{ width: `${(draws / totalMatches) * 100}%`, background: DONUT_COLORS.draw }} />
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>

                <article className="xst-card xst-cimax">
                  <header>
                    <span className="xst-label">{t("Tu CIMAX por modo", "Your CIMAX by mode")}</span>
                    {cimaxRows.best.score > 0 ? (
                      <em className="xst-chip">
                        {t("Mejor", "Best")}: {cimaxRows.best.label}
                      </em>
                    ) : null}
                  </header>
                  {cimaxRows.rows.map((row) => (
                    <div
                      key={row.key}
                      className={`xst-cimax-row${modeFilter === row.key ? " is-selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={modeFilter === row.key}
                      onClick={() => selectModeFilter(row.key)}
                      onKeyDown={handleKeySelect(row.key)}
                    >
                      <div className="xst-cimax-line">
                        <i className="xst-dot" style={{ background: row.color }} />
                        <span>{row.label}</span>
                        <b>{formatCount(row.score)}</b>
                      </div>
                      <div className="xst-bar">
                        <i style={{ width: `${(row.score / cimaxRows.max) * 100}%`, background: row.color }} />
                      </div>
                    </div>
                  ))}
                </article>
              </section>

              <section className="xst-row xst-kpis">
                {kpis.map((kpi) => (
                  <article key={kpi.key} className="xst-card xst-kpi">
                    <span className="xst-kpi-icon" aria-hidden="true">{kpi.icon}</span>
                    <div>
                      <span className="xst-label">{kpi.label}</span>
                      <b>{kpi.value}</b>
                      {kpi.extra ? <small>{kpi.extra}</small> : null}
                    </div>
                  </article>
                ))}
              </section>

              <section className="xst-row xst-row-bottom">
                <article className="xst-card xst-table-card">
                  <header>
                    <h2>{t("Rendimiento por modo", "Performance by mode")}</h2>
                    <small>{t("Toca una fila para filtrar", "Tap a row to filter")}</small>
                  </header>
                  <div className="xst-table-scroll">
                    <table className="xst-table">
                      <thead>
                        <tr>
                          <th>{t("Modo", "Mode")}</th>
                          <th>{t("Partidas", "Games")}</th>
                          <th>{t("V · D · SG", "W · L · NW")}</th>
                          <th>{t("% Victorias", "% Wins")}</th>
                          <th>{t("Fuerza máx.", "Max force")}</th>
                          <th>{t("Nivel máx.", "Max level")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {GAME_STATS_MODE_KEYS.map((key) => {
                          const row = byMode[key] || {};
                          const rowTotal = toCount(row.totalMatches);
                          const rowRate = rowTotal > 0 ? (toCount(row.wins) / rowTotal) * 100 : 0;
                          return (
                            <tr
                              key={key}
                              data-mode={key}
                              className={modeFilter === key ? "is-selected" : ""}
                              tabIndex={0}
                              aria-selected={modeFilter === key}
                              onClick={() => selectModeFilter(key)}
                              onKeyDown={handleKeySelect(key)}
                            >
                              <td>
                                <span className="xst-mode-name">
                                  <i className="xst-dot" style={{ background: MODE_META[key].color }} />
                                  {MODE_META[key].label}
                                </span>
                              </td>
                              <td className="xst-num">{formatCount(rowTotal)}</td>
                              <td className="xst-wld">
                                <span className="is-win">{formatCount(row.wins)}</span> ·{" "}
                                <span className="is-loss">{formatCount(row.losses)}</span> ·{" "}
                                <span className="is-draw">{formatCount(row.draws)}</span>
                              </td>
                              <td>
                                <span className="xst-rate">
                                  <span className="xst-bar">
                                    <i style={{ width: `${rowRate}%`, background: MODE_META[key].color }} />
                                  </span>
                                  {formatDecimal(rowRate, 1)} %
                                </span>
                              </td>
                              <td className="xst-num">{formatDecimal(row.gameStats?.maxGameForceAchieved)}</td>
                              <td className="xst-num">{formatDecimal(row.gameStats?.maxGameLevelAchieved)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {selectedMeta ? (
                    <div className="xst-detail" style={{ "--xst-mode": selectedMeta.color }}>
                      <span className="xst-label">
                        {t(`Detalle de ${selectedMeta.label}`, `${selectedMeta.label} details`)}
                      </span>
                      <div className="xst-detail-grid">
                        {detailMetrics.map(([label, value]) => (
                          <div key={label}>
                            <small>{label}</small>
                            <b>{formatDecimal(value)}</b>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </article>

                <aside className="xst-side">
                  <article className="xst-card xst-side-card">
                    <span className="xst-label">{t("Modo favorito", "Favorite mode")}</span>
                    {favoriteKey ? (
                      <>
                        <b style={{ color: MODE_META[favoriteKey].color }}>{MODE_META[favoriteKey].label}</b>
                        <small>
                          {formatCount(favoriteStats.totalMatches)} {t("partidas", "games")} ·{" "}
                          {formatPercent(favoriteStats.wins, toCount(favoriteStats.totalMatches))}{" "}
                          {t("victorias", "wins")}
                        </small>
                      </>
                    ) : (
                      <b>—</b>
                    )}
                  </article>
                  <article className="xst-card xst-side-card">
                    <span className="xst-label">{t("Capturas", "Captures")}</span>
                    <div className="xst-capture">
                      <div>
                        <b>{formatCount(summary.capturedBlocks)}</b>
                        <small>{t("bloques capturados", "blocks captured")}</small>
                      </div>
                      <div>
                        <b className="is-violet">{formatCount(summary.specialCaptures)}</b>
                        <small>{t("especiales capturadas", "special captures")}</small>
                      </div>
                    </div>
                  </article>
                  <article className="xst-card xst-side-card xst-tournaments">
                    <div>
                      <span className="xst-label">
                        {selectedMeta
                          ? t(`Torneos ganados · ${selectedMeta.label}`, `Tournaments won · ${selectedMeta.label}`)
                          : t("Torneos ganados", "Tournaments won")}
                      </span>
                      <b>{formatCount(tournamentWins)}</b>
                    </div>
                    <button type="button" className="xst-gold-btn" onClick={() => navigate("/game/play")}>
                      {t("Ver torneos", "See tournaments")} ›
                    </button>
                  </article>
                </aside>
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Stats;
